/**
 * FIPS lookup utilities for HeatmapChart.
 *
 * Baked view data frequently arrives as US state abbreviations (`'NC'`) or
 * full state names (`'North Carolina'`) rather than raw FIPS codes. This
 * module provides conversion helpers so consumers can normalise their `geoId`
 * values before passing them to `<HeatmapChart data={...} />`.
 *
 * County-level FIPS lookups are not included here because a full ~3 000-row
 * county name → FIPS table would add significant bundle weight. For county
 * data, consumers should source FIPS codes from their data pipeline.
 *
 * @example
 * // Normalise state-abbreviation data before passing to the chart
 * const normalised = rawData.map(row => ({
 *   ...row,
 *   geoId: stateAbbrevToFips(row.state) ?? row.state,
 * }));
 */

// ── State abbreviation → 2-digit FIPS ────────────────────────────────────────

/**
 * Maps every US state (+ DC, PR, VI, GU, AS, MP) abbreviation to its
 * 2-digit FIPS code string.
 */
export const STATE_ABBREV_TO_FIPS: Readonly<Record<string, string>> = {
  AL: '01',
  AK: '02',
  AZ: '04',
  AR: '05',
  CA: '06',
  CO: '08',
  CT: '09',
  DE: '10',
  DC: '11',
  FL: '12',
  GA: '13',
  HI: '15',
  ID: '16',
  IL: '17',
  IN: '18',
  IA: '19',
  KS: '20',
  KY: '21',
  LA: '22',
  ME: '23',
  MD: '24',
  MA: '25',
  MI: '26',
  MN: '27',
  MS: '28',
  MO: '29',
  MT: '30',
  NE: '31',
  NV: '32',
  NH: '33',
  NJ: '34',
  NM: '35',
  NY: '36',
  NC: '37',
  ND: '38',
  OH: '39',
  OK: '40',
  OR: '41',
  PA: '42',
  RI: '44',
  SC: '45',
  SD: '46',
  TN: '47',
  TX: '48',
  UT: '49',
  VT: '50',
  VA: '51',
  WA: '53',
  WV: '54',
  WI: '55',
  WY: '56',
  // Territories
  PR: '72',
  VI: '78',
  GU: '66',
  AS: '60',
  MP: '69',
};

/**
 * Inverse map: 2-digit FIPS → state abbreviation.
 * Derived from `STATE_ABBREV_TO_FIPS` at module load time.
 */
export const FIPS_TO_STATE_ABBREV: Readonly<Record<string, string>> =
  Object.fromEntries(
    Object.entries(STATE_ABBREV_TO_FIPS).map(([abbrev, fips]) => [
      fips,
      abbrev,
    ]),
  );

// ── State full name → 2-digit FIPS ───────────────────────────────────────────

/**
 * Maps US state full names (title-case) to their 2-digit FIPS code string.
 * Matching is case-insensitive in `stateNameToFips`.
 */
export const STATE_NAME_TO_FIPS: Readonly<Record<string, string>> = {
  Alabama: '01',
  Alaska: '02',
  Arizona: '04',
  Arkansas: '05',
  California: '06',
  Colorado: '08',
  Connecticut: '09',
  Delaware: '10',
  'District of Columbia': '11',
  Florida: '12',
  Georgia: '13',
  Hawaii: '15',
  Idaho: '16',
  Illinois: '17',
  Indiana: '18',
  Iowa: '19',
  Kansas: '20',
  Kentucky: '21',
  Louisiana: '22',
  Maine: '23',
  Maryland: '24',
  Massachusetts: '25',
  Michigan: '26',
  Minnesota: '27',
  Mississippi: '28',
  Missouri: '29',
  Montana: '30',
  Nebraska: '31',
  Nevada: '32',
  'New Hampshire': '33',
  'New Jersey': '34',
  'New Mexico': '35',
  'New York': '36',
  'North Carolina': '37',
  'North Dakota': '38',
  Ohio: '39',
  Oklahoma: '40',
  Oregon: '41',
  Pennsylvania: '42',
  'Rhode Island': '44',
  'South Carolina': '45',
  'South Dakota': '46',
  Tennessee: '47',
  Texas: '48',
  Utah: '49',
  Vermont: '50',
  Virginia: '51',
  Washington: '53',
  'West Virginia': '54',
  Wisconsin: '55',
  Wyoming: '56',
  'Puerto Rico': '72',
  'Virgin Islands': '78',
  Guam: '66',
  'American Samoa': '60',
  'Northern Mariana Islands': '69',
};

// ── Helper functions ──────────────────────────────────────────────────────────

/**
 * Pre-built lowercase name → FIPS map for O(1) lookups in `stateNameToFips`.
 * Built once at module load from `STATE_NAME_TO_FIPS`.
 */
const STATE_NAME_LOWER_TO_FIPS: Readonly<Record<string, string>> =
  Object.fromEntries(
    Object.entries(STATE_NAME_TO_FIPS).map(([name, fips]) => [
      name.toLowerCase(),
      fips,
    ]),
  );

/**
 * Converts a US state abbreviation to its 2-digit FIPS code.
 * Case-insensitive. Returns `undefined` for unrecognised inputs.
 *
 * @example
 * stateAbbrevToFips('NC') // '37'
 * stateAbbrevToFips('nc') // '37'
 * stateAbbrevToFips('XX') // undefined
 */
export function stateAbbrevToFips(abbrev: string): string | undefined {
  return STATE_ABBREV_TO_FIPS[abbrev.trim().toUpperCase()];
}

/**
 * Converts a US state full name to its 2-digit FIPS code.
 * Case-insensitive. Returns `undefined` for unrecognised inputs.
 *
 * @example
 * stateNameToFips('North Carolina') // '37'
 * stateNameToFips('north carolina') // '37'
 * stateNameToFips('Narnia')         // undefined
 */
export function stateNameToFips(name: string): string | undefined {
  return STATE_NAME_LOWER_TO_FIPS[name.trim().toLowerCase()];
}

/**
 * Converts a 2-digit FIPS code to its state abbreviation.
 * Returns `undefined` for unrecognised codes.
 *
 * @example
 * fipsToStateAbbrev('37') // 'NC'
 * fipsToStateAbbrev('06') // 'CA'
 */
export function fipsToStateAbbrev(fips: string): string | undefined {
  return FIPS_TO_STATE_ABBREV[fips.trim()];
}

/**
 * Attempts to resolve any state identifier — FIPS code, abbreviation, or full
 * name — into a canonical 2-digit FIPS string.
 *
 * Resolution order:
 * 1. Already a valid 2-digit FIPS → returned as-is.
 * 2. Matches a state abbreviation (e.g. `'NC'`) → returns FIPS.
 * 3. Matches a full state name (e.g. `'North Carolina'`) → returns FIPS.
 * 4. Unrecognised → returns `undefined`.
 *
 * @example
 * resolveStateFips('37')              // '37'  (already FIPS)
 * resolveStateFips('NC')              // '37'  (abbreviation)
 * resolveStateFips('North Carolina')  // '37'  (full name)
 * resolveStateFips('unknown')         // undefined
 */
export function resolveStateFips(value: string): string | undefined {
  const trimmed = value.trim();
  // Already a 1–2 digit numeric FIPS
  if (
    /^\d{1,2}$/.test(trimmed) &&
    FIPS_TO_STATE_ABBREV[trimmed.padStart(2, '0')]
  ) {
    return trimmed.padStart(2, '0');
  }
  return stateAbbrevToFips(trimmed) ?? stateNameToFips(trimmed);
}

/**
 * Returns a new array with `geoId` values converted from state abbreviations or
 * full names to 2-digit FIPS codes.
 * Rows whose `geoId` cannot be resolved are left unchanged (the chart will
 * emit a `console.warn` for unknown geoIds).
 *
 * @example
 * normalizeStateGeoIds([
 *   { geoId: 'NC', value: 100 },
 *   { geoId: 'California', value: 200 },
 * ])
 * // → [{ geoId: '37', value: 100 }, { geoId: '06', value: 200 }]
 */
export function normalizeStateGeoIds<T extends { geoId: string }>(
  data: T[],
): T[] {
  return data.map((row) => {
    const resolved = resolveStateFips(row.geoId);
    return resolved !== undefined ? { ...row, geoId: resolved } : row;
  });
}

/** @deprecated Use {@link normalizeStateGeoIds} (American spelling). */
export const normaliseStateGeoIds = normalizeStateGeoIds;
